import { Box, Dialog, Typography } from '@mui/material'
import _ from 'lodash'
import { useCallback, useContext, useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useDispatch, useSelector } from 'react-redux'
import { fetchServingOrder } from '~/apis/orderHistory'
import { dataCartSelector, priceTotalItemCart } from '~/redux/cartSlice'
import { ThemeContext } from '~/theme/ThemeProvider'
import { orderHistorySelector } from '~/views/OrderHistory/slices'

import { Grid } from '@mui/material'
import classNames from 'classnames/bind'
import PropTypes from 'prop-types'
import { CloseCircleIcon } from '~/assets/icons'
import { useAppContext } from '~/contexts/AppContext'
import useCancelRequest from '~/hooks/useCancelRequest'
import { selectRestaurant } from '~/redux/restaurantSlice'
import { convertPrice, getTextVat } from '~/utils'
import ExcludesVAT from '~/views/Menu/components/ExcludesVAT'
import CustomTable from '../Table'
import styles from './PopupBillTablet.module.scss'

const cx = classNames.bind(styles)

// Hàm chuyển đổi từ mảng A sang mảng B
const convertAtoB = (arrayA) => {
  let arrayB = []

  if (arrayA) {
    arrayA.forEach((item) => {
      item.requests.forEach((request) => {
        const newItem = {
          quantity: request.quantity,
          item: request.name,
          status: request.status,
          price: `${convertPrice(request.price)}`,
        }
        arrayB.push(newItem)
      })
    })
    return arrayB
  }
  return arrayB
}

const PopupBillTablet = ({ onClosePopup, open }) => {
  const { responseServingRequest, setResponseServingRequest, saveServingSessionID } =
    useAppContext()
  const dispatch = useDispatch()
  const { data: dataOrderHistory, loading, error } = useSelector(orderHistorySelector)
  const dataCart = useSelector(dataCartSelector)
  const { tableUid } = useSelector(selectRestaurant)
  const { i18n, t } = useTranslation()
  const { currentThemeObject } = useContext(ThemeContext)

  const { controller } = useCancelRequest()

  // biến đùng để hiển thị lỗi khi submit order
  const [messageErr, setMessageErr] = useState('')

  const {
    restaurant = { vat_rate: 0, service_rate: 0, menuExcludesVAT: null },
    service_fee,
    data,
    sub_total,
    total,
    vat_fee,
    extra_fee,
    discount_amount,
    table,
  } = dataOrderHistory

  const { serving_session_id } = table ?? {}
  const dataConvert = useMemo(() => convertAtoB(data), [data])

  // Lưu lại để lắng nghe socket
  useEffect(() => {
    if (serving_session_id) {
      saveServingSessionID(serving_session_id)
    }
  }, [serving_session_id, saveServingSessionID])

  const cart_items = useMemo(() => {
    return dataCart.map((item) => ({
      ...item,
      price: priceTotalItemCart(item),
    }))
  }, [dataCart])

  const debounceFetchData = useCallback(
    _.debounce(() => {
      dispatch(fetchServingOrder(controller))
    }, 200),
    [],
  )

  useEffect(() => {
    dispatch(fetchServingOrder(controller))
  }, [dispatch, i18n.language, tableUid, controller])

  // Lắng nghe khi thay đổi status thì call lại api
  useEffect(() => {
    if (responseServingRequest) {
      debounceFetchData()
    }

    // Khi rời khỏi màn hình này thì cần clear state đi tránh gọi api 2 lần
    return () => {
      setResponseServingRequest()
    }
  }, [responseServingRequest, debounceFetchData, setResponseServingRequest])

  const getErrorMessage = (err) => {
    setMessageErr(err)
  }

  /** render error message */
  const renderErrorMessage = () => {
    if (!messageErr) return null
    return (
      <Typography
        sx={{
          fontSize: '10px',
          color: 'red',
          marginBottom: '5px',
        }}>
        {i18n.t(messageErr)}
      </Typography>
    )
  }

  const renderErrorPopup = () => {
    if (error || !data || (data && data.length === 0))
      return (
        <Box display={'flex'} flex={1} bgcolor="baseColor.color1">
          <Box minHeight="10vh">
            <Typography
              marginTop="60%"
              fontSize="2.4rem"
              fontWeight="600"
              lineHeight="3rem"
              color={'baseColor.colorText1'}>
              {t('titles.order_empty')}
            </Typography>
            <Typography
              fontSize="1.6rem"
              fontWeight="400"
              lineHeight="2rem"
              color={'baseColor.colorText1'}>
              {t('descriptions.order_empty')}
            </Typography>
          </Box>
        </Box>
      )

    return null
  }

  const renderHeaderItem = (title, value) => {
    return (
      <Box display={'flex'} flexDirection={'row'}>
        <Typography fontSize="1.6rem" fontWeight="700" color="baseColor.colorText1">
          {title}
        </Typography>
        <Typography
          fontSize="1.6rem"
          fontWeight="400"
          color="baseColor.colorText1"
          paddingLeft={'3px'}>
          {value}
        </Typography>
      </Box>
    )
  }

  const renderHeaderOrder = () => {
    if (!data || data?.length === 0) return null

    return (
      <Box className={cx('header_wrapper')}>
        <Typography fontSize="4rem" fontWeight="600" color="baseColor.colorText1">
          {i18n.t('titles.order_details')}
        </Typography>
        <Box className={cx('header')}>
          {renderHeaderItem(t('titles.table_number'), table?.name)}
        </Box>
      </Box>
    )
  }
  const checkAllPending = (arr) => {
    if (!arr || arr.length === 0) {
      return false
    }
    let allCancelledOrPending = true // Giả sử ban đầu tất cả đều là "cancelled" hoặc "pending"

    arr.forEach((item) => {
      if (item.status !== 'cancelled' && item.status !== 'pending') {
        allCancelledOrPending = false // Nếu có bất kỳ trạng thái nào khác, đặt lại biến là false
      }
    })

    return allCancelledOrPending
  }

  const renderTable = () => {
    if (!data || data?.length === 0) return null
    return (
      <Box display={'flex'} flexDirection={'column'} alignItems={'flex-start'} gap={'30px'}>
        <CustomTable data={dataConvert} height={400} pageSize={5} tableWidth={'100%'} />
      </Box>
    )
  }

  const renderSumaryContent = (title, value) => {
    return (
      <Box display={'flex'} flexDirection={'column'} width={'100%'}>
        <Box display={'flex'}>
          <Typography fontSize="1.8rem" color={currentThemeObject.colorText1}>
            {title}
          </Typography>
        </Box>
        <Box display={'flex'} width={'100%'} justifyContent={'flex-end'}>
          <Typography className={cx('txt_money')} color={currentThemeObject.colorText1}>
            {value}
          </Typography>
        </Box>
      </Box>
    )
  }

  const renderDivider = () => {
    return <div className={cx('divider')}></div>
  }

  const renderTotal = () => {
    return (
      <div className={cx('total_money_wrapper')}>
        <Typography fontSize={'20px'} fontWeight={600} color={currentThemeObject.colorText1}>
          {t('titles.total')}
        </Typography>
        <Typography fontSize={'28px'} fontWeight={600} color={currentThemeObject.colorText1}>
          {convertPrice(total)}
        </Typography>
      </div>
    )
  }

  const renderRightView = () => {
    if (checkAllPending(data)) return null
    return (
      <Box display={'flex'} alignItems={'flex-start'} gap={10} width={'100%'}>
        <Box className={cx('right_wrapper')}>
          <Box display={'flex'} flexDirection={'column'} alignItems={'flex-start'} gap={'5px'}>
            <Typography fontSize={'40px'} fontWeight={600} color={currentThemeObject.colorText1}>
              {t('titles.total')}
            </Typography>
            <Typography fontSize={'20px'} fontWeight={600} color={currentThemeObject.colorText1}>
              {dataConvert?.length || 0} {t('label.items')}
            </Typography>
          </Box>
          <Box
            display={'flex'}
            flexDirection={'column'}
            alignItems={'flex-start'}
            gap={'10px'}
            width={'100%'}>
            {sub_total ? (
              <>
                {renderSumaryContent(t('titles.sub_total'), convertPrice(sub_total))}
                {renderDivider()}
              </>
            ) : null}
            {extra_fee ? (
              <>
                {renderSumaryContent(t('titles.extraFee'), convertPrice(extra_fee))}
                {renderDivider()}
              </>
            ) : null}
            {discount_amount ? (
              <>
                {renderSumaryContent(t('titles.discountAmount'), convertPrice(discount_amount))}
                {renderDivider()}
              </>
            ) : null}
            {vat_fee ? (
              <>
                {renderSumaryContent(
                  `${t(getTextVat(i18n.language))}` +
                    `${restaurant?.vat_rate > 0 ? `(${restaurant?.vat_rate}%)` : ''}`,
                  convertPrice(vat_fee),
                )}
                {renderDivider()}
              </>
            ) : null}
            {service_fee ? (
              <>
                {renderSumaryContent(
                  `${t('label.service_fee')} ` +
                    `${restaurant.service_rate > 0 ? `(${restaurant.service_rate}%)` : ''}`,
                  convertPrice(service_fee || 0),
                )}
                {renderDivider()}
              </>
            ) : null}
          </Box>
          {renderTotal()}
        </Box>
      </Box>
    )
  }

  const renderContent = () => {
    if (!data || data?.length === 0) return null
    return (
      <div className={cx('content_wrapper')}>
        <Grid container spacing={1}>
          <Grid item xs={8}>
            {renderTable()}
          </Grid>
          <Grid item xs={4}>
            {renderRightView()}
          </Grid>
        </Grid>
      </div>
    )
  }

  return (
    <Dialog
      className={cx('container')}
      open={open}
      onClose={onClosePopup}
      aria-describedby="alert-dialog-slide-description"
      PaperProps={{
        className: cx('wrapper'),
      }}>
      <div className={cx('close')} onClick={onClosePopup} aria-hidden>
        <CloseCircleIcon size="38" background="rgba(0, 0, 0, 0.75)" />
      </div>
      <Box className={cx('')} bgcolor="baseColor.color1" id="order_code">
        {renderHeaderOrder()}
        {renderContent()}
        {renderErrorPopup()}
        {data?.length > 0 && <ExcludesVAT />}
      </Box>
    </Dialog>
  )
}
PopupBillTablet.propTypes = {
  open: PropTypes.bool,

  onClosePopup: PropTypes.func,
}
export default PopupBillTablet
